import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, DollarSign, Users, Clock, Bell } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface BookingStats {
  upcoming: number;
  monthly: number;
  revenue: number;
  notifications: number;
}

export const QuickStats: React.FC = () => {
  const { user } = useAuth();
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const { data: stats, isLoading } = useQuery<BookingStats>({
    queryKey: ['quick-stats', user?.id],
    queryFn: async () => {
      // Get all bookings in a single query
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', user?.id)
        .gte('start_time', startOfMonth.toISOString());

      if (bookingsError) throw bookingsError;

      // Get unread notifications in a separate query
      const { data: notifications, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user?.id)
        .eq('status', 'unread');

      if (notificationsError) throw notificationsError;

      // Calculate stats from the bookings data
      const upcomingBookings = bookings.filter(booking => 
        new Date(booking.start_time) > now
      );

      const monthlyBookings = bookings.filter(booking => 
        new Date(booking.start_time) >= startOfMonth &&
        new Date(booking.start_time) <= endOfMonth
      );

      const revenue = monthlyBookings
        .filter(booking => booking.status === 'confirmed')
        .reduce((total, booking) => total + (booking.price || 0), 0);

      return {
        upcoming: upcomingBookings.length,
        monthly: monthlyBookings.length,
        revenue,
        notifications: notifications.length,
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60, // Consider data fresh for 1 minute
    cacheTime: 1000 * 60 * 5, // Cache data for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: 1, // Only retry once on failure
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center animate-pulse">
                <div className="h-8 w-8 bg-muted rounded-full mb-2" />
                <div className="h-8 w-16 bg-muted rounded mb-1" />
                <div className="h-4 w-24 bg-muted rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statItems = [
    {
      label: 'Upcoming',
      value: stats?.upcoming || 0,
      icon: Calendar,
      color: 'text-blue-500',
    },
    {
      label: 'Monthly',
      value: stats?.monthly || 0,
      icon: Users,
      color: 'text-green-500',
    },
    {
      label: 'Revenue',
      value: formatCurrency(stats?.revenue || 0),
      icon: DollarSign,
      color: 'text-yellow-500',
    },
    {
      label: 'Notifications',
      value: stats?.notifications || 0,
      icon: Bell,
      color: 'text-purple-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {statItems.map((item, index) => (
        <Card key={index}>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <item.icon className={`h-8 w-8 ${item.color} mb-2`} />
              <h3 className="text-3xl font-bold">{item.value}</h3>
              <p className="text-sm text-muted-foreground">{item.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}; 